import { type MessageDescriptor } from 'react-intl';

import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

export type TeamTabValue = 'it-support' | 'it-operations' | 'engineering' | 'hr';

export type TeamNavigationConfig = {
	avatarAppearance: 'default' | 'outlined';
	avatarInitial: 'A' | 'B' | 'P';
	showAskRovo: boolean;
	showBack: boolean;
	showCreate: boolean;
	showCustomizeHelpCenter: boolean;
	showSearch: boolean;
};

export type TeamTab = {
	label: MessageDescriptor;
	value: TeamTabValue;
};

export const teamTabs: TeamTab[] = [
	{ label: messages.teamItSupport, value: 'it-support' },
	{ label: messages.teamItOperations, value: 'it-operations' },
	{ label: messages.teamEngineering, value: 'engineering' },
	{ label: messages.teamHr, value: 'hr' },
];

export const teamNavigationConfig: Record<TeamTabValue, TeamNavigationConfig> = {
	'it-support': {
		avatarAppearance: 'outlined',
		avatarInitial: 'A',
		showAskRovo: false,
		showBack: true,
		showCreate: false,
		showCustomizeHelpCenter: true,
		showSearch: false,
	},
	'it-operations': {
		avatarAppearance: 'default',
		avatarInitial: 'P',
		showAskRovo: true,
		showBack: false,
		showCreate: true,
		showCustomizeHelpCenter: false,
		showSearch: true,
	},
	engineering: {
		avatarAppearance: 'default',
		avatarInitial: 'A',
		showAskRovo: true,
		showBack: false,
		showCreate: true,
		showCustomizeHelpCenter: false,
		showSearch: true,
	},
	hr: {
		avatarAppearance: 'default',
		avatarInitial: 'B',
		showAskRovo: true,
		showBack: false,
		showCreate: true,
		showCustomizeHelpCenter: false,
		showSearch: true,
	},
};

export type ArticleAccentColor = 'lime' | 'teal';

export type HelpArticleId = 'laptop' | 'vpn' | 'password';

const lastUpdated: Record<HelpArticleId, string> = {
	laptop: '2025-03-04',
	vpn: '2025-11-15',
	password: '2025-07-22',
};

export type HelpCard = {
	id: HelpArticleId;
	tag: MessageDescriptor;
	tagAppearance: 'accent-lime' | 'accent-blue';
	title: MessageDescriptor;
	description: MessageDescriptor;
	lastUpdated: string;
};

export const itSupportHelpCards: HelpCard[] = [
	{
		id: 'laptop',
		tag: messages.cardLaptopTag,
		tagAppearance: 'accent-lime',
		title: messages.cardLaptopTitle,
		description: messages.cardLaptopDesc,
		lastUpdated: lastUpdated.laptop,
	},
	{
		id: 'vpn',
		tag: messages.cardVpnTag,
		tagAppearance: 'accent-blue',
		title: messages.cardVpnTitle,
		description: messages.cardVpnDesc,
		lastUpdated: lastUpdated.vpn,
	},
	{
		id: 'password',
		tag: messages.cardPasswordTag,
		tagAppearance: 'accent-blue',
		title: messages.cardPasswordTitle,
		description: messages.cardPasswordDesc,
		lastUpdated: lastUpdated.password,
	},
];

export type RedactedParagraph = number[];

const leadParagraph: RedactedParagraph = [1, 0.62];
const tipParagraph: RedactedParagraph = [0.8];

export type PortalArticleSection = {
	heading: MessageDescriptor;
	paragraphs: RedactedParagraph[];
};

export type PortalArticle = {
	id: HelpArticleId;
	title: MessageDescriptor;
	intro: MessageDescriptor;
	lastUpdated: string;
	sections: PortalArticleSection[];
};

export const portalArticles: Record<HelpArticleId, PortalArticle> = {
	laptop: {
		id: 'laptop',
		title: messages.articleLaptopTitle,
		intro: messages.articleLaptopIntro,
		lastUpdated: lastUpdated.laptop,
		sections: [
			{ heading: messages.articleLaptopSection1, paragraphs: [leadParagraph, tipParagraph] },
			{ heading: messages.articleLaptopSection2, paragraphs: [leadParagraph] },
		],
	},
	vpn: {
		id: 'vpn',
		title: messages.articleVpnTitle,
		intro: messages.articleVpnIntro,
		lastUpdated: lastUpdated.vpn,
		sections: [
			{ heading: messages.articleVpnSection1, paragraphs: [leadParagraph, tipParagraph] },
			{ heading: messages.articleVpnSection2, paragraphs: [leadParagraph] },
		],
	},
	password: {
		id: 'password',
		title: messages.articlePasswordTitle,
		intro: messages.articlePasswordIntro,
		lastUpdated: lastUpdated.password,
		sections: [
			{ heading: messages.articlePasswordSection1, paragraphs: [leadParagraph, tipParagraph] },
			{ heading: messages.articlePasswordSection2, paragraphs: [leadParagraph] },
		],
	},
};
